import type * as XLSX from "xlsx";
import { createDownloadLink } from "../../util/util";

export type ExportCell = { text: string; href?: string };

export function readTableCells(table: HTMLTableElement): ExportCell[][] {
  const rows: ExportCell[][] = [];
  table.querySelectorAll("tr").forEach((tr) => {
    const cells: ExportCell[] = [];
    tr.querySelectorAll("th, td").forEach((cell) => {
      const text = (cell.textContent || "").replace(/\s+/g, " ").trim();
      const anchor = cell.querySelector("a");
      const href = anchor?.getAttribute("href") || undefined;
      cells.push(href ? { text, href } : { text });
    });
    if (cells.length > 0) rows.push(cells);
  });
  return rows;
}

export function timestampedFilename(
  options: "csv" | "xlsx" | { chatBotName: string; ext: "csv" | "xlsx" }
): string {
  const { chatBotName, ext } =
    typeof options === "string"
      ? { chatBotName: "agilis", ext: options }
      : options;
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  const stamp =
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `-${pad(d.getHours())}${pad(d.getMinutes())}`;
  return `${chatBotName}-table-${stamp}.${ext}`;
}

function toAbsoluteHref(href: string): string {
  return href.startsWith("/") ? `${window.location.origin}${href}` : href;
}

function escapeCsvField(value: string): string {
  // Spreadsheet apps can execute formula-like CSV cells even when quoted.
  // Keep catalog identifiers as text, consistent with the XLSX string cells.
  // Control prefixes are intentional here: spreadsheet parsers can ignore them.
  // eslint-disable-next-line no-control-regex
  if (/^[\s\u0000-\u001f]*[=+\-@]/.test(value) || /^[\t\r\n]/.test(value)) {
    value = `'${value}`;
  }
  if (/[",\r\n]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

export function rowsToCsv(rows: ExportCell[][]): string {
  return rows
    .map((row) => row.map((c) => escapeCsvField(c.text)).join(","))
    .join("\r\n");
}

export function exportCsv(
  input: HTMLTableElement | { table: HTMLTableElement; chatBotName: string },
  chatBotName = "agilis"
) {
  const { table, name } =
    input instanceof HTMLTableElement
      ? { table: input, name: chatBotName }
      : { table: input.table, name: input.chatBotName };
  exportCsvRows(readTableCells(table), name);
}

export function exportCsvRows(rows: ExportCell[][], chatBotName = "agilis") {
  if (rows.length === 0) return;
  // createDownloadLink prepends a UTF-8 BOM so Excel opens the file correctly.
  const link = createDownloadLink({
    name: timestampedFilename({ chatBotName, ext: "csv" }),
    type: "text/csv;charset=utf-8",
    data: rowsToCsv(rows),
  });
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(link.href);
}

export function buildXlsxWorkbook(
  xlsx: typeof XLSX,
  rows: ExportCell[][]
): XLSX.WorkBook {
  const data = rows.map((r) => r.map((c) => c.text));
  const ws = xlsx.utils.aoa_to_sheet(data);

  // Force every cell to string type and attach hyperlinks where present.
  // Excel will otherwise coerce things like "203-585-2" into a date.
  rows.forEach((row, r) => {
    row.forEach((cell, c) => {
      const addr = xlsx.utils.encode_cell({ r, c });
      const ref = ws[addr];
      if (!ref) return;
      ref.t = "s";
      ref.v = cell.text;
      if (isSafeExportHref(cell.href)) {
        const target = toAbsoluteHref(cell.href as string);
        ref.l = { Target: target, Tooltip: target };
      }
    });
  });

  const wb = xlsx.utils.book_new();
  xlsx.utils.book_append_sheet(wb, ws, "Sheet1");
  return wb;
}

export async function exportXlsx(
  input: HTMLTableElement | { table: HTMLTableElement; chatBotName: string },
  chatBotName = "agilis"
) {
  const { table, name } =
    input instanceof HTMLTableElement
      ? { table: input, name: chatBotName }
      : { table: input.table, name: input.chatBotName };
  return exportXlsxRows(readTableCells(table), name);
}

export function isSafeExportHref(href: string | undefined): boolean {
  return !!href && (/^\/(?![/\\])/.test(href) || /^https?:\/\//i.test(href));
}

export async function exportXlsxRows(
  rows: ExportCell[][],
  chatBotName = "agilis"
) {
  if (rows.length === 0) return;
  const xlsx = await import("xlsx");
  const wb = buildXlsxWorkbook(xlsx, rows);
  xlsx.writeFile(wb, timestampedFilename({ chatBotName, ext: "xlsx" }));
}
